import { useState, useEffect, useCallback, CSSProperties } from 'react';
import { useHaptics } from '../../hooks/useHaptics';

export interface ButtonOption {
  value: string;      // Data value (e.g., 'good', 'fair')
  label: string;      // Display label (e.g., 'Good', 'Fair')
}

interface ConditionButtonGroupProps {
  id: string;
  label: string;
  options: ButtonOption[];
  value: string | null;              // Current data value (e.g., 'good', 'fair-poor')
  onChange: (value: string | null) => void;
  allowRange?: boolean;              // Default true
  colorScale?: 'paint' | 'growth' | 'anode';
  lastValue?: string | null;         // Previous service value to display as reference
}

// Paint condition buttons (6 primary states)
export const PAINT_BUTTON_OPTIONS: ButtonOption[] = [
  { value: 'not_inspected', label: 'Not Inspected' },
  { value: 'excellent', label: 'Excellent' },
  { value: 'good', label: 'Good' },
  { value: 'fair', label: 'Fair' },
  { value: 'poor', label: 'Poor' },
  { value: 'missing', label: 'Missing' },
];

// Growth level buttons (6 primary states)
export const GROWTH_BUTTON_OPTIONS: ButtonOption[] = [
  { value: 'not_inspected', label: 'Not Inspected' },
  { value: 'minimal', label: 'Minimal' },
  { value: 'moderate', label: 'Moderate' },
  { value: 'heavy', label: 'Heavy' },
  { value: 'severe', label: 'Severe' },
  { value: 'severe-maximum', label: 'Maximum' },
];

// Anode condition buttons (single selection only)
export const ANODE_BUTTON_OPTIONS: ButtonOption[] = [
  { value: '100', label: '100%' },
  { value: '75', label: '75%' },
  { value: '50', label: '50%' },
  { value: '25', label: '25%' },
  { value: '10', label: '10%' },
  { value: '0', label: 'Missing' },
];

// Parse a data value into selected indices
function parseValueToIndices(value: string | null, options: ButtonOption[]): number[] {
  if (!value || value === 'not_inspected') {
    // Check if first option is 'not_inspected'
    if (options[0]?.value === 'not_inspected' && value === 'not_inspected') {
      return [0];
    }
    return [];
  }

  // Check for exact match first
  const exactIndex = options.findIndex(opt => opt.value === value);
  if (exactIndex !== -1) {
    return [exactIndex];
  }

  // Check for range value (e.g., 'fair-poor')
  const parts = value.split('-');
  if (parts.length === 2) {
    const firstIndex = options.findIndex(opt => opt.value === parts[0]);
    const secondIndex = options.findIndex(opt => opt.value === parts[1]);
    if (firstIndex !== -1 && secondIndex !== -1) {
      return [Math.min(firstIndex, secondIndex), Math.max(firstIndex, secondIndex)];
    }
  }

  return [];
}

// Convert selected indices to data value
function indicesToValue(indices: number[], options: ButtonOption[]): string | null {
  if (indices.length === 0) return null;
  if (indices.length === 1) return options[indices[0]]?.value || null;

  // Two selected - create range value (lower index first = better condition first)
  const sorted = [...indices].sort((a, b) => a - b);
  const first = options[sorted[0]]?.value;
  const second = options[sorted[1]]?.value;

  if (!first || !second) return null;

  // Return as "better-worse" (lower index = better condition)
  return `${first}-${second}`;
}

// Get display text for current selection
function getDisplayText(indices: number[], options: ButtonOption[]): string {
  if (indices.length === 0) return 'None';
  if (indices.length === 1) return options[indices[0]]?.label || 'None';

  const sorted = [...indices].sort((a, b) => a - b);
  return `${options[sorted[0]]?.label} - ${options[sorted[1]]?.label}`;
}

// Get button colors based on selection and color scale
function getButtonColors(
  index: number,
  isSelected: boolean,
  isInRange: boolean,
  colorScale: 'paint' | 'growth' | 'anode' | undefined,
  totalOptions: number
): { background: string; color: string; border: string } {
  if (!isSelected && !isInRange) {
    return { background: '#f3f4f6', color: '#374151', border: '#d1d5db' };
  }

  const percent = index / (totalOptions - 1);

  if (colorScale === 'paint') {
    // Paint: green (excellent) -> yellow -> red (missing)
    if (index === 0) return { background: '#6b7280', color: 'white', border: '#4b5563' }; // Not inspected
    if (percent < 0.4) return { background: '#22c55e', color: 'white', border: '#16a34a' };
    if (percent < 0.7) return { background: '#eab308', color: 'white', border: '#ca8a04' };
    return { background: '#ef4444', color: 'white', border: '#dc2626' };
  }

  if (colorScale === 'growth') {
    // Growth: blue (minimal) -> dark blue -> almost black (maximum)
    if (index === 0) return { background: '#6b7280', color: 'white', border: '#4b5563' }; // Not inspected
    if (percent < 0.3) return { background: '#38bdf8', color: 'white', border: '#0ea5e9' };
    if (percent < 0.5) return { background: '#3b82f6', color: 'white', border: '#2563eb' };
    if (percent < 0.7) return { background: '#1d4ed8', color: 'white', border: '#1e40af' };
    return { background: '#1e293b', color: 'white', border: '#0f172a' };
  }

  if (colorScale === 'anode') {
    // Anode: green (100%) -> yellow -> red (missing)
    if (percent < 0.3) return { background: '#22c55e', color: 'white', border: '#16a34a' };
    if (percent < 0.6) return { background: '#eab308', color: 'white', border: '#ca8a04' };
    return { background: '#ef4444', color: 'white', border: '#dc2626' };
  }

  // Default
  return { background: '#3b82f6', color: 'white', border: '#2563eb' };
}

// Styles
const styles: Record<string, CSSProperties> = {
  container: {
    marginBottom: '24px',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '8px',
  },
  label: {
    fontSize: '14px',
    fontWeight: 600,
    color: '#374151',
  },
  valueDisplay: {
    fontSize: '14px',
    fontWeight: 600,
    padding: '2px 8px',
    borderRadius: '4px',
  },
  valueDisplayEmpty: {
    backgroundColor: '#f3f4f6',
    color: '#6b7280',
  },
  valueDisplayActive: {
    backgroundColor: '#dbeafe',
    color: '#1d4ed8',
  },
  buttonGroup: {
    display: 'flex',
    flexWrap: 'wrap' as const,
    gap: '8px',
  },
  button: {
    padding: '8px 12px',
    borderRadius: '8px',
    borderWidth: '2px',
    borderStyle: 'solid',
    fontWeight: 500,
    fontSize: '14px',
    minHeight: '44px',
    minWidth: '60px',
    cursor: 'pointer',
    transition: 'all 0.15s ease',
  },
  clearButton: {
    padding: '8px 12px',
    borderRadius: '8px',
    borderWidth: '2px',
    borderStyle: 'solid',
    fontWeight: 500,
    fontSize: '14px',
    minHeight: '44px',
    cursor: 'pointer',
    backgroundColor: 'white',
    color: '#4b5563',
    borderColor: '#d1d5db',
    marginLeft: 'auto',
  },
};

// Helper to format lastValue for display
function formatLastValue(lastValue: string | null | undefined, options: ButtonOption[]): string | null {
  if (!lastValue) return null;

  // Find the option with matching value
  const option = options.find(opt => opt.value === lastValue);
  if (option) return option.label;

  // Check for range value (e.g., 'fair-poor')
  const parts = lastValue.split('-');
  if (parts.length === 2) {
    const first = options.find(opt => opt.value === parts[0]);
    const second = options.find(opt => opt.value === parts[1]);
    if (first && second) return `${first.label} - ${second.label}`;
  }

  // Fallback: capitalize and replace underscores
  return lastValue.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

export function ConditionButtonGroup({
  id,
  label,
  options,
  value,
  onChange,
  allowRange = true,
  colorScale,
  lastValue,
}: ConditionButtonGroupProps) {
  const { lightTap } = useHaptics();
  const [selectedIndices, setSelectedIndices] = useState<number[]>(() =>
    parseValueToIndices(value, options)
  );

  // Sync with external value changes
  useEffect(() => {
    const newIndices = parseValueToIndices(value, options);
    setSelectedIndices(newIndices);
  }, [value, options]);

  const handleButtonClick = useCallback(
    (clickedIndex: number) => {
      lightTap(); // Haptic feedback
      let newIndices: number[];

      // Check if "not_inspected" is involved (always index 0 if present)
      const isNotInspectedIndex = options[0]?.value === 'not_inspected' ? 0 : -1;
      const clickedIsNotInspected = clickedIndex === isNotInspectedIndex;

      if (selectedIndices.length === 0) {
        // Nothing selected - select clicked
        newIndices = [clickedIndex];
      } else if (selectedIndices.length === 1) {
        const currentIndex = selectedIndices[0];
        const currentIsNotInspected = currentIndex === isNotInspectedIndex;

        if (currentIndex === clickedIndex) {
          // Same button - deselect
          newIndices = [];
        } else if (allowRange && !clickedIsNotInspected && !currentIsNotInspected) {
          // Different button, ranges allowed, neither is "not_inspected" - create range
          newIndices = [currentIndex, clickedIndex];
        } else {
          // Ranges not allowed or "not_inspected" involved - replace selection
          newIndices = [clickedIndex];
        }
      } else {
        // Two selected - clear and select new
        newIndices = [clickedIndex];
      }

      setSelectedIndices(newIndices);
      const newValue = indicesToValue(newIndices, options);
      onChange(newValue);
    },
    [selectedIndices, allowRange, options, onChange, lightTap]
  );

  const handleClear = useCallback(() => {
    lightTap(); // Haptic feedback
    setSelectedIndices([]);
    onChange(null);
  }, [onChange, lightTap]);

  // Determine if each button is selected or in range
  const getButtonState = (index: number) => {
    const isSelected = selectedIndices.includes(index);
    let isInRange = false;

    if (selectedIndices.length === 2) {
      const [min, max] = [
        Math.min(...selectedIndices),
        Math.max(...selectedIndices),
      ];
      isInRange = index > min && index < max;
    }

    return { isSelected, isInRange };
  };

  const displayText = getDisplayText(selectedIndices, options);
  const lastDisplayText = formatLastValue(lastValue, options);

  return (
    <div style={styles.container} id={id}>
      <div style={styles.header}>
        <div>
          <label style={styles.label}>{label}</label>
          {lastDisplayText && (
            <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>
              Last: {lastDisplayText}
            </div>
          )}
        </div>
        <span
          style={{
            ...styles.valueDisplay,
            ...(selectedIndices.length === 0 ? styles.valueDisplayEmpty : styles.valueDisplayActive),
          }}
        >
          {displayText}
        </span>
      </div>

      <div style={styles.buttonGroup}>
        {options.map((option, index) => {
          const { isSelected, isInRange } = getButtonState(index);
          const colors = getButtonColors(
            index,
            isSelected,
            isInRange,
            colorScale,
            options.length
          );

          return (
            <button
              key={option.value}
              type="button"
              data-value={option.value}
              data-index={index}
              onClick={() => handleButtonClick(index)}
              style={{
                ...styles.button,
                backgroundColor: colors.background,
                color: colors.color,
                borderColor: colors.border,
                boxShadow: isSelected || isInRange ? '0 4px 6px -1px rgba(0, 0, 0, 0.1)' : '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
              }}
            >
              {option.label}
            </button>
          );
        })}

        {/* Clear button */}
        <button
          type="button"
          onClick={handleClear}
          style={styles.clearButton}
        >
          Clear
        </button>
      </div>
    </div>
  );
}

// Export for backwards compatibility - map button selection to slider value
export function buttonValueToSliderValue(
  buttonValue: string | null,
  type: 'paint' | 'growth'
): number {
  if (!buttonValue) return 0;

  if (type === 'paint') {
    const paintMap: Record<string, number> = {
      'not_inspected': 0,
      'excellent': 1,
      'excellent-good': 2,
      'good': 3,
      'good-fair': 4,
      'fair': 5,
      'fair-poor': 6,
      'poor': 7,
      'poor-missing': 8,
      'missing': 9,
    };
    return paintMap[buttonValue] ?? 0;
  }

  if (type === 'growth') {
    const growthMap: Record<string, number> = {
      'not_inspected': 0,
      'minimal': 1,
      'minimal-moderate': 2,
      'moderate': 3,
      'moderate-heavy': 4,
      'heavy': 5,
      'heavy-severe': 6,
      'severe': 7,
      'severe-maximum': 8,
    };
    return growthMap[buttonValue] ?? 0;
  }

  return 0;
}

// Convert slider value back to button value
export function sliderValueToButtonValue(
  sliderValue: number,
  type: 'paint' | 'growth'
): string {
  if (type === 'paint') {
    const paintValues = [
      'not_inspected', 'excellent', 'excellent-good', 'good', 'good-fair',
      'fair', 'fair-poor', 'poor', 'poor-missing', 'missing'
    ];
    return paintValues[sliderValue] || 'not_inspected';
  }

  if (type === 'growth') {
    const growthValues = [
      'not_inspected', 'minimal', 'minimal-moderate', 'moderate', 'moderate-heavy',
      'heavy', 'heavy-severe', 'severe', 'severe-maximum'
    ];
    return growthValues[sliderValue] || 'not_inspected';
  }

  return 'not_inspected';
}
